import { fluidItemStyles } from '../fluid-layout.js';
import type { WebEditorElementFrame as LandingEditorElementFrame } from '../layout.js';

type Viewport = 'desktop' | 'mobile';

const sizingClasses = (frame: LandingEditorElementFrame, viewport: Viewport) => {
  const item = viewport === 'desktop' ? frame.autoLayoutItemDesktop : frame.autoLayoutItemMobile;
  if (!item) return [];
  return [
    `vp-auto-item-${viewport}`,
    `vp-auto-width-${item.widthMode}-${viewport}`,
    `vp-auto-height-${item.heightMode}-${viewport}`,
  ];
};

export const autoLayoutNodeClasses = (frame: LandingEditorElementFrame | undefined) => {
  if (!frame) return [];
  const classes: string[] = [];
  if (frame.fluidLayout) classes.push('vp-fluid-layout');
  if (frame.fluidSize) classes.push('vp-fluid-item', ...(frame.fluidSize.height ? ['vp-fluid-media'] : []));
  if (frame.autoLayoutDesktop) classes.push('vp-auto-frame-desktop', `vp-auto-flow-${frame.autoLayoutDesktop.flow}-desktop`, `vp-auto-frame-width-${frame.autoLayoutDesktop.widthMode}-desktop`, `vp-auto-frame-height-${frame.autoLayoutDesktop.heightMode}-desktop`);
  if (frame.autoLayoutMobile) classes.push('vp-auto-frame-mobile', `vp-auto-flow-${frame.autoLayoutMobile.flow}-mobile`, `vp-auto-frame-width-${frame.autoLayoutMobile.widthMode}-mobile`, `vp-auto-frame-height-${frame.autoLayoutMobile.heightMode}-mobile`);
  return [...classes, ...sizingClasses(frame, 'desktop'), ...sizingClasses(frame, 'mobile')];
};

const frameStyles = (frame: LandingEditorElementFrame, viewport: Viewport) => {
  const layout = viewport === 'desktop' ? frame.autoLayoutDesktop : frame.autoLayoutMobile;
  if (!layout) return [];
  return [
    `--vp-auto-gap-x-${viewport}: ${layout.gapX}px;`,
    `--vp-auto-gap-y-${viewport}: ${layout.gapY}px;`,
    `--vp-auto-pad-x-${viewport}: ${layout.paddingX}px;`,
    `--vp-auto-pad-y-${viewport}: ${layout.paddingY}px;`,
    `--vp-auto-align-x-${viewport}: ${layout.alignX};`,
    `--vp-auto-align-y-${viewport}: ${layout.alignY};`,
    `--vp-auto-columns-${viewport}: ${layout.columns ?? 12};`,
    ...(layout.fixedWidth ? [`--vp-auto-frame-width-${viewport}: ${layout.fixedWidth}px;`] : []),
    ...(layout.fixedHeight ? [`--vp-auto-frame-height-${viewport}: ${layout.fixedHeight}px;`] : []),
  ];
};

const itemStyles = (frame: LandingEditorElementFrame, viewport: Viewport) => {
  const item = viewport === 'desktop' ? frame.autoLayoutItemDesktop : frame.autoLayoutItemMobile;
  if (!item) return [];
  return [
    `--vp-auto-order-${viewport}: ${item.order};`,
    `--vp-auto-column-span-${viewport}: ${item.columnSpan ?? 1};`,
    `--vp-auto-row-span-${viewport}: ${item.rowSpan ?? 1};`,
    ...(item.fixedWidth ? [`--vp-auto-item-width-${viewport}: ${item.fixedWidth}px;`] : []),
    ...(item.fixedHeight ? [`--vp-auto-item-height-${viewport}: ${item.fixedHeight}px;`] : []),
  ];
};

export const autoLayoutNodeStyles = (frame: LandingEditorElementFrame | undefined) =>
  frame
    ? [...(frame.fluidSize ? fluidItemStyles(frame.fluidSize) : []), ...frameStyles(frame, 'desktop'), ...frameStyles(frame, 'mobile'), ...itemStyles(frame, 'desktop'), ...itemStyles(frame, 'mobile')]
    : [];
